import React, { useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { CopyAll, Delete, Update, WhereToVote } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { useAuth } from '../../session/InternalAuthProvider';
import { AccountData, OrderData } from '../../api';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { isRescheduleEnabledForOrderPage } from '../../utils/AccountUtils';
import CheckinDialog from '../dialogs/CheckinDialog/CheckinDialog';
import PickupNotificationButton from '../dialogs/PickupNotificationButton';
import CopyToClipboardButton from '../dialogs/CopyToClipboardButton';
import { useOrders } from '../swr/useOrders';

type OrderActionsBarProps = {
  order: OrderData;
  onAfterAction?: () => void;
  onOrderUpdated?: (order: OrderData) => void;
};

export const OrderActionsBar = ({ order, onAfterAction, onOrderUpdated }: OrderActionsBarProps) => {
  const { t } = useTranslation();
  const { isAtLeastSupervisor } = UseAuthPermissions();
  const { openEditOrderDrawer, openDeleteOrderDialog, startDate, endDate } = useCalendar();
  const { updateItem: updateOrder } = useOrders({ startDate, endDate });
  // @ts-ignore
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const [checkInDialogIsOpen, setCheckInDialogIsOpen] = useState(false);

  const handleOrderUpdate = async (itemToUpdate: OrderData) => {
    await updateOrder(itemToUpdate);
    onOrderUpdated?.(itemToUpdate);
  };

  return (
    <>
      <IconButton onClick={() => openEditOrderDrawer({ order, cloneOrder: true })}>
        <Tooltip title={t('cloneOrder')} arrow>
          <CopyAll />
        </Tooltip>
      </IconButton>
      <IconButton
        onClick={() => {
          setCheckInDialogIsOpen(true);
        }}>
        <Tooltip title={t('checkin')} arrow>
          <WhereToVote />
        </Tooltip>
      </IconButton>
      <PickupNotificationButton order={order} account={account} onOrderUpdate={handleOrderUpdate} />
      <Tooltip
        title={
          !isAtLeastSupervisor
            ? t('noPermissions')
            : isRescheduleEnabledForOrderPage(account, order.order_page_id)
            ? t('amend')
            : t('amend_disabled')
        }
        arrow>
        <Box component="span" display="inline-flex" p={1}>
          <CopyToClipboardButton
            IconComponent={Update}
            text={`${import.meta.env.VITE_REACT_APP_APP_DOMAIN}/manage-booking?orderid=${order.id}`}
            orderPageId={order.order_page_id}
            disabled={!isAtLeastSupervisor || !isRescheduleEnabledForOrderPage(account, order.order_page_id)}
          />
        </Box>
      </Tooltip>
      <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : t('delete')} arrow>
        <span>
          <IconButton
            disabled={!isAtLeastSupervisor}
            onClick={() => {
              openDeleteOrderDialog({ order });
              onAfterAction?.();
            }}>
            <Delete />
          </IconButton>
        </span>
      </Tooltip>
      <CheckinDialog
        order={order}
        isOpen={checkInDialogIsOpen}
        setIsOpen={setCheckInDialogIsOpen}
        updateItem={handleOrderUpdate}
      />
    </>
  );
};
